import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  Language,
  ExpandMore,
  OpenInNew,
  Lock as LockIcon,
  PictureAsPdf as PdfIcon,
  MusicNote as MusicNoteIcon,
  School as SchoolIcon,
  Chat as ChatIcon
} from '@mui/icons-material';

const API_BASE_URL = 'https://api.labs.jeshua.ong';
const IDIOMAS = { es: 'Español', en: 'English', pt: 'Português' };

const LockedAction = ({ icon, label, tooltip }) => (
  <Tooltip title={tooltip} arrow>
    <span>
      <IconButton
        disabled
        size="small"
        aria-label={label}
        sx={{ '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' } }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
          <LockIcon sx={{ position: 'absolute', fontSize: '9px', bottom: -1, right: -3, color: '#ff5722' }} />
        </Box>
      </IconButton>
    </span>
  </Tooltip>
);

const LockedActionBar = ({ t }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: { xs: 0, sm: 0.5 } }}>
    <LockedAction icon={<PdfIcon fontSize="small" />} label={t('locked.pdf')} tooltip={t('locked.tooltip')} />
    <LockedAction icon={<MusicNoteIcon fontSize="small" />} label={t('locked.music')} tooltip={t('locked.tooltip')} />
    <LockedAction icon={<SchoolIcon fontSize="small" />} label={t('locked.teachings')} tooltip={t('locked.tooltip')} />
    <LockedAction icon={<ChatIcon fontSize="small" />} label={t('locked.chat')} tooltip={t('locked.tooltip')} />
  </Box>
);

const ChannelingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState('es');
  const [langAnchor, setLangAnchor] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(`${API_BASE_URL}/canalizaciones/${id}?lang=${lang}`)
      .then(res => {
        if (!res.ok) throw new Error('Contenido no encontrado');
        return res.json();
      })
      .then(responseData => {
        setData(responseData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching detail:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [id, lang]);

  const btnStyle = {
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    '&:hover': { backgroundColor: 'rgba(255,87,34,0.15)', borderColor: '#ff5722' },
    borderRadius: 1,
    textTransform: 'none'
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 10 }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Container sx={{ mt: 10, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: '#fff', mb: 3 }}>
          {t('detail.not_available')}
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={btnStyle}>
          {t('detail.back')}
        </Button>
      </Container>
    );
  }

  const idiomasDisponibles = data.idiomas || ['es'];
  const firstParrafoIndex = (data.bloques || []).findIndex(b => b.tipo === 'parrafo');

  return (
    <Container component="main" sx={{ mt: 2, mb: 6, maxWidth: '900px !important' }}>

      {/* Barra superior */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={btnStyle}>
          {t('detail.back')}
        </Button>

        <Box>
          <Button
            onClick={(e) => setLangAnchor(e.currentTarget)}
            startIcon={<Language />}
            endIcon={<ExpandMore />}
            sx={btnStyle}
          >
            {lang.toUpperCase()}
          </Button>
          <Menu
            anchorEl={langAnchor}
            open={Boolean(langAnchor)}
            onClose={() => setLangAnchor(null)}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(66,74,74,0.98)',
                border: '1px solid rgba(255,255,255,0.2)',
                minWidth: 150,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
              }
            }}
          >
            {Object.entries(IDIOMAS).map(([code, name]) => (
              <MenuItem
                key={code}
                disabled={!idiomasDisponibles.includes(code)}
                onClick={() => { setLang(code); setLangAnchor(null); }}
                sx={{
                  color: lang === code ? '#ff5722' : 'rgba(255,255,255,0.9)',
                  backgroundColor: lang === code ? 'rgba(255,87,34,0.2)' : 'transparent',
                  fontWeight: lang === code ? 700 : 400,
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { backgroundColor: 'rgba(255,87,34,0.15)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' }
                }}
              >
                {name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {/* Tarjeta de contenido */}
      <Box sx={{ backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 2, p: { xs: 3, md: 5 } }}>

        {/* Botonera superior */}
        <LockedActionBar t={t} />

        {/* Imagen destacada */}
        {data.imagen && (
          <Box
            component="img"
            src={data.imagen}
            alt={data.titulo}
            onError={(e) => { e.target.style.display = 'none'; }}
            sx={{ width: '100%', maxHeight: 600, objectFit: 'contain', borderRadius: 2, mt: 2, mb: 5, display: 'block' }}
          />
        )}

        {/* Título */}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontSize: { xs: '1.8rem', md: '2.5rem' },
            fontWeight: 700,
            mb: 3,
            lineHeight: 1.2,
            color: '#fff',
            textAlign: 'center',
            pb: 2.5,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 3,
              background: 'linear-gradient(90deg, transparent, #ff5722, transparent)',
              borderRadius: 1
            }
          }}
        >
          {data.titulo}
        </Typography>

        {/* Meta header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto auto',
            justifyContent: 'flex-end',
            columnGap: 1.5,
            rowGap: 0.5,
            pb: 3.5,
            mb: 4.5,
            borderBottom: '2px solid rgba(255,255,255,0.1)'
          }}
        >
          <Typography sx={{ textAlign: 'right', color: 'rgba(255,255,255,0.95)', fontSize: '0.95rem' }}>
            {t('detail.message_from')}
          </Typography>
          <Typography sx={{ textAlign: 'left', color: '#ff5722', fontSize: '0.95rem', fontWeight: 700 }}>
            {data.canalizador}
          </Typography>
          {data.canalizadorHumano && (
            <>
              <Typography sx={{ textAlign: 'right', color: 'rgba(255,255,255,0.95)', fontSize: '0.95rem' }}>
                {t('detail.through')}
              </Typography>
              <Typography sx={{ textAlign: 'left', color: 'rgba(255,255,255,0.95)', fontSize: '0.95rem', fontWeight: 700 }}>
                {data.canalizadorHumano}
              </Typography>
            </>
          )}
        </Box>

        {/* Bloques de contenido */}
        <Box>
          {(data.bloques || []).map((bloque, index) => {
            if (bloque.tipo === 'parrafo') {
              const isFirst = index === firstParrafoIndex;
              return (
                <Typography
                  key={index}
                  paragraph
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.9,
                    color: 'rgba(255,255,255,0.9)',
                    mb: 3,
                    ...(isFirst && {
                      '&::first-letter': {
                        fontSize: '3.5rem',
                        fontWeight: 700,
                        color: '#ff5722',
                        float: 'left',
                        lineHeight: 1,
                        marginRight: '8px',
                        marginTop: '4px'
                      }
                    })
                  }}
                >
                  {bloque.texto}
                </Typography>
              );
            }
            if (bloque.tipo === 'cita') {
              return (
                <Box
                  key={index}
                  sx={{
                    borderLeft: '3px solid #ff5722',
                    px: 3,
                    py: 2.5,
                    my: 4,
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    lineHeight: 1.7
                  }}
                >
                  <Typography sx={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.95)', fontStyle: 'italic', lineHeight: 1.7 }}>
                    {bloque.texto}
                  </Typography>
                </Box>
              );
            }
            if (bloque.tipo === 'firma') {
              return (
                <Typography
                  key={index}
                  sx={{
                    textAlign: 'left',
                    fontSize: '1.2rem',
                    color: '#ff5722',
                    fontStyle: 'italic',
                    mt: 5,
                    pt: 3,
                    pb: 3,
                    borderTop: '1px solid rgba(255,255,255,0.15)',
                    borderBottom: '1px solid rgba(255,255,255,0.15)',
                    fontFamily: 'Georgia, serif',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {bloque.texto}
                </Typography>
              );
            }
            return null;
          })}
        </Box>

        {/* Footer de metadata */}
        {(data.musica?.url || data.video?.url || data.adaptadoPor) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 3, pb: 3, borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, textAlign: 'right' }}>
              {data.musica?.url && (
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, marginRight: 4 }}>{t('detail.music')}:</strong>
                  <Box component="a" href={data.musica.url} target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover': { color: '#ff5722', textDecoration: 'underline' } }}>
                    {data.musica.titulo}
                    <OpenInNew sx={{ fontSize: 14, opacity: 0.7 }} />
                  </Box>
                </Typography>
              )}
              {data.video?.url && (
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, marginRight: 4 }}>{t('detail.video')}:</strong>
                  <Box component="a" href={data.video.url} target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover': { color: '#ff5722', textDecoration: 'underline' } }}>
                    {data.video.titulo}
                    <OpenInNew sx={{ fontSize: 14, opacity: 0.7 }} />
                  </Box>
                </Typography>
              )}
              {data.adaptadoPor && (
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, marginRight: 4 }}>{t('detail.adapted_by')}:</strong>
                  {data.adaptadoPor}<sup style={{ fontSize: '0.7em' }}>®</sup>
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Botonera inferior */}
        <Box sx={{ mt: 3 }}>
          <LockedActionBar t={t} />
        </Box>

      </Box>
    </Container>
  );
};

export default ChannelingDetailPage;
